import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface AddCalendarEventProps {
  selectedDate: Date | null;
  onAddEvent: (event: any) => void;
  refreshCalendar: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const AddCalendarEvent: React.FC<AddCalendarEventProps> = ({ selectedDate, onAddEvent, refreshCalendar, isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(isOpen || false);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Handle visibility for animations
  React.useEffect(() => {
    if (isOpen !== undefined) {
      setIsModalOpen(isOpen);
      if (isOpen) {
        setTimeout(() => setIsVisible(true), 10);
      } else {
        setIsVisible(false);
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) {
        onClose();
      } else {
        setIsModalOpen(false);
      }
      resetForm();
    }, 300);
  };
  const [formData, setFormData] = useState({
    title: '',
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    startTime: '18:00',
    endTime: '19:00',
    color: '#3B82F6',
    bookable: 0,
    repeat: 'none',
    repeatEnd: '',
    cotrainerId: '',
    taetigkeitsbericht: false,
    taetigkeitsberichtText: ''
  });

  // Update date when selectedDate changes
  React.useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        date: format(selectedDate, 'yyyy-MM-dd')
      }));
    }
  }, [selectedDate]);

  const eventColors = [
    { value: '#3B82F6', label: 'Blau' },
    { value: '#10B981', label: 'Grün' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' },
    { value: '#8B5CF6', label: 'Violett' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#6B7280', label: 'Grau' }
  ];

  // Helper function to add 1 hour to a time string
  const addOneHour = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const newHours = (hours + 1) % 24;
    return `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime: string) => {
    const newEndTime = addOneHour(newStartTime);
    setFormData({ ...formData, startTime: newStartTime, endTime: newEndTime });
  };

  // Add one week to date
  const addOneWeek = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 7);
    return format(date, 'yyyy-MM-dd');
  };

  // Handle repeat change
  const handleRepeatChange = (newRepeat: string) => {
    if (newRepeat !== 'none' && formData.repeatEnd === '') {
      const endDate = addOneWeek(formData.date);
      setFormData({ ...formData, repeat: newRepeat, repeatEnd: endDate });
    } else {
      setFormData({ ...formData, repeat: newRepeat });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const eventData = {
        title: formData.title,
        start: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        color: formData.color,
        bookable: formData.bookable,
        repeat: formData.repeat,
        repeat_end: formData.repeatEnd || null,
        cotrainer_id: formData.cotrainerId || null,
        taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
        taetigkeitsbericht_text: formData.taetigkeitsbericht ? formData.taetigkeitsberichtText : null
      };

      const response = await apiClient.post('/api/calendar', eventData);
      
      if (response.data.success) {
        const newEvent = {
          id: response.data.event_id,
          title: formData.title,
          start: new Date(`${formData.date}T${formData.startTime}`),
          end: new Date(`${formData.date}T${formData.endTime}`),
          color: formData.color,
          bookable: formData.bookable,
          bookingCount: 0,
          creator: user?.vorname + ' ' + user?.nachname,
          creator_id: user?.mg_nr,
          repeat: formData.repeat,
          repeat_end: formData.repeatEnd,
          taetigkeitsbericht: formData.taetigkeitsbericht ? 1 : 0,
          taetigkeitsbericht_text: formData.taetigkeitsberichtText
        };
        
        onAddEvent(newEvent);
        refreshCalendar();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating event:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      startTime: '18:00',
      endTime: '19:00',
      color: '#3B82F6',
      bookable: 0,
      repeat: 'none',
      repeatEnd: '',
      cotrainerId: '',
      taetigkeitsbericht: false,
      taetigkeitsberichtText: ''
    });
  };

  // If controlled from parent, only show modal
  if (isOpen !== undefined) {
    if (!isModalOpen) return null;
    
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 z-50"
          onClick={handleClose}
        />
        
        {/* Modal Container */}
        <div className={`
          fixed z-50
          inset-x-0 bottom-0
          transform transition-transform duration-300 ease-out
          ${isVisible ? 'translate-y-0' : 'translate-y-full'}
          
          /* Desktop positioning - oben */
          sm:inset-x-0 sm:top-20 sm:bottom-auto sm:flex sm:justify-center sm:p-4
          sm:transform-none
        `}>
          <div className={`
            /* Mobile: Full width, slide from bottom */
            w-full max-h-[90vh] flex flex-col
            bg-white dark:bg-surface-dark
            rounded-t-2xl
            
            /* Desktop: Centered with max-width */
            sm:max-w-2xl sm:rounded-2xl
            shadow-xl
            
            /* Desktop animation */
            sm:transition-all sm:duration-300
            ${isVisible ? 'sm:scale-100 sm:opacity-100' : 'sm:scale-95 sm:opacity-0'}
          `}>
            {/* Gripper for Mobile */}
            <div className="sm:hidden sticky top-0 bg-white dark:bg-surface-dark pt-2 pb-1 z-10 rounded-t-2xl">
              <div className="mx-auto w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            {/* Header - mit dynamischer Farbe */}
            <div
              className="border-b border-gray-200 dark:border-gray-700 p-6 sm:rounded-t-2xl"
              style={{ backgroundColor: formData.color }}
            >
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white truncate pr-4">
                  {formData.title.trim() || 'Neuen Termin erstellen'}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="h-5 w-5 text-white/80 hover:text-white" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Titel *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  placeholder="z.B. Training, Agility, Vorstandssitzung"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Datum *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Farbe
                  </label>
                  <div className="flex gap-2">
                    {eventColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color.value ? 'border-gray-900 dark:border-white' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Startzeit *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Endzeit *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max. Teilnehmer
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(formData.bookable)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, bookable: val === '' ? 0 : parseInt(val) });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.bookable === 0 ? 'Termin nicht buchbar' : `${formData.bookable} Teilnehmer`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Wiederholung
                </label>
                <select
                  value={formData.repeat}
                  onChange={(e) => handleRepeatChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="none">Keine Wiederholung</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="biweekly">Alle 2 Wochen</option>
                  <option value="monthly">Monatlich</option>
                </select>
              </div>

              {formData.repeat !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Wiederholung endet am
                  </label>
                  <input
                    type="date"
                    value={formData.repeatEnd}
                    onChange={(e) => setFormData({ ...formData, repeatEnd: e.target.value })}
                    min={formData.date}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              )}

              {/* Tätigkeitsbericht */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="taetigkeitsbericht"
                    checked={formData.taetigkeitsbericht}
                    onChange={(e) => setFormData({ ...formData, taetigkeitsbericht: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="taetigkeitsbericht" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Im Tätigkeitsbericht erwähnen
                  </label>
                </div>
                {formData.taetigkeitsbericht && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Beschreibung für Tätigkeitsbericht
                    </label>
                    <textarea
                      value={formData.taetigkeitsberichtText}
                      onChange={(e) => setFormData({ ...formData, taetigkeitsberichtText: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                      rows={2}
                      placeholder="z.B. Jahreshauptversammlung mit Vorstandswahlen"
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.taetigkeitsberichtText.length}/500 Zeichen
                    </p>
                  </div>
                )}
              </div>

              {/* Footer with buttons */}
              <div className="sticky bottom-0 bg-white dark:bg-surface-dark border-t border-gray-200 dark:border-gray-700 p-4 rounded-b-2xl sm:rounded-b-2xl">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-3 py-2.5 text-sm font-medium whitespace-nowrap bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-3 py-2.5 text-sm font-medium whitespace-nowrap bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Erstellen...' : 'Erstellen'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }

  // Original behavior with button
  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        title="Termin hinzufügen"
      >
        <PlusIcon className="h-5 w-5" />
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div
              className="sticky top-0 border-b border-gray-200 dark:border-gray-700 p-6 rounded-t-2xl"
              style={{ backgroundColor: formData.color }}
            >
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white truncate pr-4">
                  {formData.title.trim() || 'Neuen Termin erstellen'}
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="h-5 w-5 text-white/80 hover:text-white" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Titel *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  placeholder="z.B. Training, Agility, Vorstandssitzung"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Datum *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Farbe
                  </label>
                  <div className="flex gap-2">
                    {eventColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color.value ? 'border-gray-900 dark:border-white' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Startzeit *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Endzeit *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max. Teilnehmer
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(formData.bookable)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, bookable: val === '' ? 0 : parseInt(val) });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.bookable === 0 ? 'Termin nicht buchbar' : `${formData.bookable} Teilnehmer`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Wiederholung
                </label>
                <select
                  value={formData.repeat}
                  onChange={(e) => handleRepeatChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="none">Keine Wiederholung</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="biweekly">Alle 2 Wochen</option>
                  <option value="monthly">Monatlich</option>
                </select>
              </div>

              {formData.repeat !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Wiederholung endet am
                  </label>
                  <input
                    type="date"
                    value={formData.repeatEnd}
                    onChange={(e) => setFormData({ ...formData, repeatEnd: e.target.value })}
                    min={formData.date}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              )}

              {/* Tätigkeitsbericht */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="taetigkeitsbericht2"
                    checked={formData.taetigkeitsbericht}
                    onChange={(e) => setFormData({ ...formData, taetigkeitsbericht: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="taetigkeitsbericht2" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Im Tätigkeitsbericht erwähnen
                  </label>
                </div>
                {formData.taetigkeitsbericht && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Beschreibung für Tätigkeitsbericht
                    </label>
                    <textarea
                      value={formData.taetigkeitsberichtText}
                      onChange={(e) => setFormData({ ...formData, taetigkeitsberichtText: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-white"
                      rows={2}
                      placeholder="z.B. Jahreshauptversammlung mit Vorstandswahlen"
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.taetigkeitsberichtText.length}/500 Zeichen
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="flex-1 px-3 py-2.5 text-sm font-medium whitespace-nowrap bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-3 py-2.5 text-sm font-medium whitespace-nowrap bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Erstellen...' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AddCalendarEvent;